import type { FC, ReactNode } from 'react';
import { useMemo } from 'react';
import {
  InstantSearch,
  SearchBox,
  Configure,
} from 'react-instantsearch-hooks-web';
import styled from 'styled-components';

import { useHeatMapFilterOptions } from '../../map-data/use-heat-map-filter-options';
import { algoliaSearchClient } from '../../algolia-search';

const SearchContainer = styled.div`
  position: relative;
`;

interface HeatMapSearchProps {
  children: ReactNode;
  placeholder: string;
}

export const HeatMapSearch: FC<HeatMapSearchProps> = ({
  children,
  placeholder,
}) => {
  const { filterOptions } = useHeatMapFilterOptions();
  const heatMapIndex = process.env.REACT_APP_ALGOLIA_INDEX_HEATMAP || '';

  const facetFilters = useMemo(() => {
    const filters: (string | string[])[] = [];

    // Handle status filter (can be array or single value)
    if (filterOptions.status) {
      if (Array.isArray(filterOptions.status)) {
        // OR logic for multiple statuses - each status becomes a separate filter option
        if (filterOptions.status.length > 0) {
          filters.push(
            filterOptions.status.map(status => `countryStatus:${status}`),
          );
        }
      } else {
        filters.push(`countryStatus:${filterOptions.status}`);
      }
    }

    // Handle continent filter
    if (filterOptions.continent) {
      filters.push(`continent:${filterOptions.continent}`);
    }

    if (filterOptions.nccpAvailable) {
      filters.push(`nccpAvailable:${filterOptions.nccpAvailable}`);
    }

    if (filterOptions.lungCancerInNccp) {
      filters.push(`lungCancerInNccp:${filterOptions.lungCancerInNccp}`);
    }

    if (filterOptions.earlyDetectionInNccp) {
      filters.push(
        `earlyDetectionInNccp:${filterOptions.earlyDetectionInNccp}`,
      );
    }

    if (filterOptions.fundingForEarlyDetection) {
      filters.push(
        `fundingForEarlyDetection:${filterOptions.fundingForEarlyDetection}`,
      );
    }

    return filters;
  }, [filterOptions]);

  const numericFilters = useMemo(() => {
    const filters: string[] = [];

    if (filterOptions.nccpYearMax !== undefined) {
      filters.push(`nccpYear<=${filterOptions.nccpYearMax}`);
    }

    return filters;
  }, [filterOptions.nccpYearMax]);

  return (
    <InstantSearch searchClient={algoliaSearchClient} indexName={heatMapIndex}>
      <Configure
        facetFilters={facetFilters}
        numericFilters={numericFilters.length > 0 ? numericFilters : undefined}
      />
      <SearchContainer>
        <SearchBox placeholder={placeholder} />
        {children}
      </SearchContainer>
    </InstantSearch>
  );
};
